import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Link2Off, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { beoordeelAdhoc, datumLabel, duurLabel, getAdhocAkkoord } from '../services/adhocUrenApi';

/**
 * Publieke accordering van ad-hoc uren (migratie 163), via de link uit de SMS
 * (/u/:token, medewerker) of de e-mail (/uren-akkoord/:token, contactpersoon).
 * Geen login. Openen keurt niets goed — pas de knop doet dat, zodat
 * linkscanners en voorvertoningen geen akkoord kunnen geven.
 */
const sleutel = (token: string) => `belagom-adhoc-klaar:${token}`;
const leesUitkomst = (token: string): 'akkoord' | 'afwijzen' | null => {
  try {
    const v = sessionStorage.getItem(sleutel(token));
    return v === 'akkoord' || v === 'afwijzen' ? v : null;
  } catch {
    return null;
  }
};
const zetUitkomst = (token: string, v: 'akkoord' | 'afwijzen') => {
  try {
    sessionStorage.setItem(sleutel(token), v);
  } catch {
    /* privémodus: een reload toont dan de "link niet meer geldig"-melding */
  }
};

const Schil: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50 px-4 py-8">
    <div className="mx-auto max-w-md">{children}</div>
  </div>
);

export const AdhocAkkoordPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [uitkomst, setUitkomst] = useState(() => leesUitkomst(token));
  const [afwijzen, setAfwijzen] = useState(false);
  const [reden, setReden] = useState('');
  const [deel5Delen, setDeel5Delen] = useState(false);

  const verzoek = useQuery({
    queryKey: ['adhoc-akkoord', token],
    queryFn: () => getAdhocAkkoord(token),
    enabled: !uitkomst,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const beoordeel = useMutation({
    mutationFn: (besluit: 'akkoord' | 'afwijzen') => beoordeelAdhoc(token, besluit, reden.trim() || undefined, besluit === 'akkoord' && deel5Delen),
    onSuccess: (_d, besluit) => {
      zetUitkomst(token, besluit);
      setUitkomst(besluit);
    },
  });

  if (uitkomst) {
    const akkoord = uitkomst === 'akkoord';
    const Icoon = akkoord ? CheckCircle2 : XCircle;
    return (
      <Schil>
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm" role="status">
          <Icoon className={`mx-auto h-14 w-14 ${akkoord ? 'text-green-600' : 'text-gray-500'}`} aria-hidden />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">{akkoord ? 'Bedankt, bevestigd!' : 'Afgewezen'}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {akkoord
              ? 'De uren zijn bevestigd. De link is nu vervallen; je hoeft verder niets te doen.'
              : 'De professional ziet dat de uren niet kloppen. De link is nu vervallen.'}
          </p>
        </div>
      </Schil>
    );
  }

  if (verzoek.isLoading) {
    return (
      <Schil>
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0B1F3A]" aria-label="Laden…" /></div>
      </Schil>
    );
  }

  if (verzoek.isError || !verzoek.data) {
    const melding = (verzoek.error as any)?.response?.data?.message;
    return (
      <Schil>
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm" role="alert">
          <Link2Off className="mx-auto h-12 w-12 text-gray-400" aria-hidden />
          <h1 className="mt-4 text-xl font-bold text-gray-900">Deze link is niet (meer) geldig</h1>
          <p className="mt-2 text-sm text-gray-600">{melding ?? 'Zijn de uren al bevestigd of afgewezen? Dan is de link direct vervallen.'}</p>
        </div>
      </Schil>
    );
  }

  const v = verzoek.data;
  const medewerker = v.kanaal === 'SMS';
  const fout = (beoordeel.error as any)?.response?.data?.message;

  return (
    <Schil>
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-gray-900">{medewerker ? 'Klopt deze afspraak?' : 'Akkoord voor deze uren?'}</h1>
        <p className="mt-1 text-sm text-gray-600">
          {v.professional_naam}{v.organisatie && !medewerker ? ` · ${v.organisatie}` : ''} vraagt je om onderstaande uren te bevestigen.
        </p>

        <dl className="mt-5 divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
          <div className="flex justify-between gap-4 px-4 py-3"><dt className="text-gray-500">Datum</dt><dd className="font-medium text-right">{datumLabel(v.datum)}</dd></div>
          <div className="flex justify-between gap-4 px-4 py-3"><dt className="text-gray-500">Duur</dt><dd className="font-medium text-right">{duurLabel(v.tijd_minuten)}</dd></div>
          <div className="flex justify-between gap-4 px-4 py-3"><dt className="text-gray-500">Soort</dt><dd className="font-medium text-right">{v.categorie_label}</dd></div>
          {v.toelichting && <div className="px-4 py-3"><dt className="text-gray-500">Toelichting</dt><dd className="mt-1 whitespace-pre-line">{v.toelichting}</dd></div>}
        </dl>

        {medewerker && (
          <div className="mt-4 flex gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-900">
            <ShieldCheck className="h-4 w-4 flex-shrink-0" aria-hidden />
            <p>Anoniem: je werkgever ziet je naam of nummer nergens. Op de factuur staat alleen een onherleidbaar transactienummer.</p>
          </div>
        )}

        {medewerker && v.deel5 && !afwijzen && (
          <div className="mt-4 rounded-lg border border-gray-200 p-3 text-sm space-y-2">
            <p className="font-semibold text-gray-900">Advies voor HR (optioneel delen)</p>
            <p className="text-xs text-gray-600">Dit is het enige wat HR te zien krijgt, en alleen als je het hieronder aanvinkt.</p>
            <p><span className="font-medium">Arbo-kader:</span> {v.deel5.arbo_kader}</p>
            {v.deel5.fte_belastbaarheid != null && (
              <p><span className="font-medium">Belastbaarheid:</span> {v.deel5.fte_belastbaarheid}% · <span className="font-medium">Verwachte verzuimduur:</span> {v.deel5.verwachte_verzuimreductie}</p>
            )}
            <p className="whitespace-pre-wrap">{v.deel5.werkvloer_advies}</p>
            <label className="flex items-start gap-2 pt-1">
              <input type="checkbox" className="mt-0.5 rounded border-gray-300" checked={deel5Delen} onChange={(e) => setDeel5Delen(e.target.checked)} />
              <span>Ik geef toestemming om dit advies met HR / mijn leidinggevende te delen</span>
            </label>
          </div>
        )}

        {fout && <p className="mt-4 text-sm text-red-600" role="alert">{fout}</p>}

        {!afwijzen ? (
          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => beoordeel.mutate('akkoord')}
              disabled={beoordeel.isPending}
              className="w-full rounded-xl bg-green-600 px-4 py-4 text-lg font-bold text-white shadow-sm hover:bg-green-700 disabled:opacity-50"
            >
              {beoordeel.isPending ? 'Bezig…' : medewerker ? '🟢 Ja, dit klopt' : '🟢 Akkoord & Factureer'}
            </button>
            <button type="button" onClick={() => setAfwijzen(true)} className="w-full py-2 text-sm text-gray-600 underline">
              Nee, dit klopt niet
            </button>
          </div>
        ) : (
          <form
            className="mt-6 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              beoordeel.mutate('afwijzen');
            }}
          >
            <label className="block text-sm">
              <span className="text-gray-700">Wat klopt er niet? <span className="text-gray-400">(optioneel)</span></span>
              <textarea className="mt-1 w-full rounded-md border-gray-300" rows={3} maxLength={500} value={reden} onChange={(e) => setReden(e.target.value)} />
            </label>
            <button type="submit" disabled={beoordeel.isPending} className="w-full rounded-lg bg-gray-800 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
              Afwijzen
            </button>
            <button type="button" onClick={() => setAfwijzen(false)} className="w-full py-2 text-sm text-gray-600 underline">Terug</button>
          </form>
        )}
      </div>
    </Schil>
  );
};

export default AdhocAkkoordPage;
